import { forwardRef, useEffect, useRef } from 'react';
import { styled, Chip as MaterialChip, ChipProps } from '@mui/material';
import { clsx } from 'clsx';
import { useMemoizedDataIds } from '../shared/utils/dataId';
import { Icon } from '../icon/Icon';
import { IconButton } from '../button/IconButton/IconButton';
import { CHIP_DATA_IDS } from './chipDataId';

export interface ChipExtendedProps extends ChipProps {
  dataId: string;
  iconName?: string;
  selected?: boolean;
  withText?: boolean;
}

// styling mui base icon button to match up HD styling
export const Chip = styled(
  forwardRef<HTMLElement, ChipExtendedProps>(
    ({ dataId, iconName, selected, withText, ...props }: ChipExtendedProps, ref) => {
      const dataIds = useMemoizedDataIds(CHIP_DATA_IDS, dataId);

      const iconButtonRef = useRef(null);

      useEffect(() => {
        if (!ref) {
          return;
        }

        if (typeof ref === 'function') {
          ref(iconButtonRef.current);
        } else {
          // eslint-disable-next-line no-param-reassign
          ref.current = iconButtonRef.current;
        }
      });

      return (
        <MaterialChip
          {...props}
          data-id={dataIds.container}
          ref={iconButtonRef}
          icon={iconName ? <Icon name={iconName} dataId={dataIds.container} /> : undefined}
          className={clsx(props.className, selected && 'selected')}
          style={withText ? { marginTop: '-2px', marginBottom: '-2px' } : undefined}
          deleteIcon={
            <IconButton dataId={dataIds.deleteBtn} size='small' label='Remove'>
              <Icon name='close' size={0} dataId={dataIds.deleteBtn} />
            </IconButton>
          }
        />
      );
    }
  )
)({
  '&.MuiChip-root': {
    backgroundColor: 'var(--ripple-bg-color)',
    padding: 'var(--spacer-2) var(--spacer-12)',
    height: 'auto',
    '&.MuiChip-clickable': {
      '&:hover, &:active, &.Mui-focusVisible': {
        backgroundColor: 'var(--divider-color)',
        boxShadow: 'none'
      }
    },
    '&.selected': {
      backgroundColor: 'var(--primary-color)',
      '&:hover, &:active, &.Mui-focusVisible': {
        backgroundColor: 'var(--primary-dark-color)'
      },
      '.MuiChip-label': {
        color: 'var(--light-text)'
      }
    }
  },
  '&.MuiChip-sizeLarge': {
    padding: 'var(--spacer-4) var(--spacer-12)'
  },
  '.MuiChip-icon': {
    marginLeft: 0,
    marginRight: 'var(--spacer-8)',
    fontSize: 'var(--icon-size-14)'
  },
  '.MuiChip-deleteIcon': {
    marginRight: 0,
    marginLeft: 'var(--spacer-8)',
    color: 'var(--text-default-color)',
    '&:hover': {
      color: 'var(--text-default-color)'
    }
  },
  '.MuiChip-label': {
    fontSize: 'var(--font-size-body)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--text-default-color)',
    lineHeight: 'var(--line-height-body)',
    padding: 0
  },
  '.MuiChip-labelSmall': {
    fontSize: 'var(--font-size-caption)',
    lineHeight: 'var(--line-height-caption)'
  },
  '.MuiChip-iconSmall': {
    fontSize: 'var(--icon-size-12)'
  },
  '&.MuiChip-colorError': {
    '.MuiChip-label': {
      color: 'var(--error-color)'
    },
    '.MuiChip-icon': {
      color: 'var(--error-color)'
    }
  }
});
